import React from 'react';

// HistoryPanel — the layout shared by every "history + add a comment" dialog (task history,
// project history, PCR notes & history).
//
// Why this exists. Six dialogs hand-rolled the same two-pane arrangement as `<Col xs={8}>` +
// `<Col xs={4}>`. An `xs` size applies at EVERY width, so on a 375px phone the timeline was
// 250px wide and the comment box was a ~125px column beside it — the composer's button sat
// below the fold of its own cramped column, which is the reported "the add comment button is
// only visible after scrolling". The fixed `height: 500px` on the scroll region made it worse
// inside a modal that is itself height-bounded.
//
// The arrangement here:
//   • phone  — one column, composer FIRST so it needs no scrolling to reach, timeline beneath
//   • md+    — timeline left, composer in a right-hand aside with the dividing rule
// The timeline is the only scrolling region and it flexes to the height it is given, so the
// panel works in a content-sized modal and a fixed-height one alike.
//
// Callers keep rendering their own entries: the history sources have genuinely different shapes
// and formatting helpers, and normalising them here would be a data change dressed up as a
// layout one. What is shared is the arrangement, the scroll region and the spacing — and, via
// `StatusBadge`, the stage colour that all six used to decide for themselves.

export interface HistoryPanelProps {
  /** The timeline itself — usually a `ListGroup` of entries, or an empty-state `Alert`. */
  children: React.ReactNode;
  /** The add-comment form, or the notice shown when the user may not comment. Omit for read-only. */
  composer?: React.ReactNode;
  /** Heading above the timeline. */
  timelineTitle?: React.ReactNode;
  /** Heading above the composer. */
  composerTitle?: React.ReactNode;
  className?: string;
}

export const HistoryPanel: React.FC<HistoryPanelProps> = ({
  children,
  composer,
  timelineTitle = 'History Timeline',
  composerTitle = 'Add New Comment',
  className = '',
}) => (
  // min-h-0 at every level is what lets the timeline's overflow actually engage instead of the
  // whole panel growing past the modal body.
  <div className={`tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-4 md:tw-flex-row ${className}`}>
    <section className="tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col">
      {timelineTitle && <h6 className="tw-mb-2 tw-shrink-0">{timelineTitle}</h6>}
      {/* The one scrolling region. Given a floor so a single-entry history is not a sliver, but
          no ceiling — the modal bounds it. */}
      <div className="tw-min-h-[8rem] tw-min-w-0 tw-flex-1 tw-overflow-y-auto tw-overscroll-contain">
        {children}
      </div>
    </section>

    {composer && (
      // order-first on a phone, order-last at md+: the composer is short, so putting it above
      // the long timeline is what makes it reachable without scrolling. The rule and padding
      // only apply once it is actually a side column.
      <aside className="tw-order-first tw-shrink-0 md:tw-order-last md:tw-w-1/3 md:tw-min-w-[14rem] md:tw-border-l md:tw-border-line md:tw-pl-4">
        {composerTitle && <h6 className="tw-mb-2">{composerTitle}</h6>}
        {composer}
      </aside>
    )}
  </div>
);

export default HistoryPanel;
